import { useEffect, useState } from 'react';

// beforeinstallprompt standart lib tiplerinde yok; ihtiyacımız olan kısmı tanımlıyoruz.
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

// Tek kaynak: yükleme olayı bir kez ateşlenir; hem ana sayfadaki bant hem panel
// butonu aynı modül durumunu kullansın diye burada saklıyoruz.
let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((fn) => fn());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    installed = true;
    deferredPrompt = null;
    emit();
  });
}

export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as unknown as { standalone?: boolean }).standalone === true;
}
export function isIos() {
  return typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function usePwaInstall() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick((t) => t + 1);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);

  const promptInstall = async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
    if (!deferredPrompt) return 'unavailable';
    await deferredPrompt.prompt();
    let outcome: 'accepted' | 'dismissed' = 'dismissed';
    try { outcome = (await deferredPrompt.userChoice).outcome; } catch { /* yoksay */ }
    deferredPrompt = null;
    emit();
    return outcome;
  };

  return {
    canInstall: !!deferredPrompt && !installed,
    installed,
    standalone: isStandalone(),
    ios: isIos(),
    promptInstall,
  };
}

const GENERIC_HINT = 'Tarayıcının menüsünden “Ana ekrana ekle” / “Uygulamayı yükle”yi seçin.';
const IOS_HINT = 'Safari’de alttaki Paylaş ⬆ → “Ana Ekrana Ekle”.';

// Panel kenar çubuğunda görünen "Uygulamayı indir" butonu.
export function InstallButton() {
  const { canInstall, installed: done, standalone, ios, promptInstall } = usePwaInstall();
  const [hint, setHint] = useState('');

  if (standalone || done) return null; // zaten uygulama olarak çalışıyor / yeni yüklendi

  const onClick = async () => {
    if (canInstall) {
      const result = await promptInstall();
      if (result === 'unavailable') setHint(ios ? IOS_HINT : GENERIC_HINT);
      return;
    }
    setHint(ios ? IOS_HINT : GENERIC_HINT);
  };

  return (
    <div className="panel-install-wrap">
      <button type="button" className="panel-install" onClick={onClick}>
        <i>⤓</i> Uygulamayı indir
      </button>
      {hint && <small className="panel-install-hint">{hint}</small>}
    </div>
  );
}
